import { ActivitiesPaginateResponse } from 'api';

export interface ShareholderEmailSetting {
  shareholder_id: number;
  send_email: boolean;
}

export type ShareholderEmailSelectionById = Record<number, boolean>;

export type DistributionActivity = ActivitiesPaginateResponse & {
  shareholder_id?: number | null;
  from_shareholder_id?: number | null;
};

export interface DistributionActivitiesListLike {
  pages: { data?: DistributionActivity[] }[];
}

const toValidShareholderId = (value?: number | null): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }

  return value;
};

export const getShareholderIdFromDistributionActivity = (
  activity: DistributionActivity,
  shareholderIdByEntityId: Record<number, number>,
): number | undefined => {
  const directShareholderId =
    toValidShareholderId(activity.shareholder_id) ??
    toValidShareholderId(activity.from_shareholder_id);

  if (directShareholderId) {
    return directShareholderId;
  }

  const mappedShareholderId = activity.from_entity_id
    ? toValidShareholderId(shareholderIdByEntityId[activity.from_entity_id])
    : undefined;

  if (mappedShareholderId) {
    return mappedShareholderId;
  }

  return toValidShareholderId(activity.from_entity_id);
};

export const collectShareholderIdsFromActivities = (
  issuerActivitiesList: DistributionActivitiesListLike | undefined,
  shareholderIdByEntityId: Record<number, number>,
): number[] => {
  if (!issuerActivitiesList) {
    return [];
  }

  const ids = new Set<number>();
  issuerActivitiesList.pages.forEach(page => {
    page.data?.forEach(activity => {
      const shareholderId = getShareholderIdFromDistributionActivity(activity, shareholderIdByEntityId);
      if (shareholderId) {
        ids.add(shareholderId);
      }
    });
  });

  return [...ids];
};

export const hydrateMissingShareholderEmailSelections = (
  prev: ShareholderEmailSelectionById,
  shareholderIds: number[],
): ShareholderEmailSelectionById => {
  let hasChanges = false;
  const next = { ...prev };

  shareholderIds.forEach(shareholderId => {
    if (!(shareholderId in next)) {
      next[shareholderId] = true;
      hasChanges = true;
    }
  });

  return hasChanges ? next : prev;
};

export const applyShareholderEmailSelectionToMany = (
  prev: ShareholderEmailSelectionById,
  shareholderIds: number[],
  checked: boolean,
): ShareholderEmailSelectionById => {
  const next = { ...prev };

  shareholderIds.forEach(shareholderId => {
    next[shareholderId] = checked;
  });

  return next;
};

export const updateShareholderEmailSelection = (
  prev: ShareholderEmailSelectionById,
  shareholderId: number,
  checked: boolean,
): ShareholderEmailSelectionById => ({
  ...prev,
  [shareholderId]: checked,
});

export const getShareholderEmailSelectionFromActivity = (
  activity: DistributionActivity,
  shareholderIdByEntityId: Record<number, number>,
  emailSelectionByShareholderId: ShareholderEmailSelectionById,
): boolean => {
  const shareholderId = getShareholderIdFromDistributionActivity(activity, shareholderIdByEntityId);

  if (!shareholderId) {
    return true;
  }

  return emailSelectionByShareholderId[shareholderId] ?? true;
};

export const mergeShareholderEmailSettings = (
  paymentSelections: ShareholderEmailSelectionById,
  reinvestmentSelections: ShareholderEmailSelectionById,
): ShareholderEmailSetting[] => {
  const mergedSelectionByShareholderId = new Map<number, boolean>();

  const mergeSettings = (settings: ShareholderEmailSelectionById) => {
    Object.entries(settings).forEach(([shareholderIdString, sendEmail]) => {
      const shareholderId = Number(shareholderIdString);
      if (!Number.isFinite(shareholderId) || shareholderId <= 0) {
        return;
      }

      if (!mergedSelectionByShareholderId.has(shareholderId)) {
        mergedSelectionByShareholderId.set(shareholderId, sendEmail);
        return;
      }

      const prevSelection = mergedSelectionByShareholderId.get(shareholderId) ?? true;

      // If the shareholder appears in both steps, unchecked wins.
      mergedSelectionByShareholderId.set(shareholderId, prevSelection && sendEmail);
    });
  };

  mergeSettings(paymentSelections);
  mergeSettings(reinvestmentSelections);

  return [...mergedSelectionByShareholderId.entries()]
    .sort(([a], [b]) => a - b)
    .map(([shareholder_id, send_email]) => ({
      shareholder_id,
      send_email,
    }));
};
